/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Lock, Mail, KeyRound, Save, ShieldCheck, AlertCircle } from "lucide-react";

const STRICT_KEY = "tcr_platform_strict_unlock_v1";

/**
 * iter78 — Platform Admin Passcode Management.
 *
 * Lives inside the Account tab when the super_admin is in Platform scope.
 * Exposes:
 *  - Change passcode (requires current).
 *  - Set / update recovery email (used by the public reset-link flow).
 *  - Toggle "require passcode at every switch" — sessionStorage flag that
 *    re-prompts even within the same browser session, useful for shared
 *    computers. Stored client-side; per-browser, not per-account.
 *  - Help text on how recovery works.
 */
export default function PlatformPasscodeManager() {
    const [cfg, setCfg] = useState(null);
    const [savingPass, setSavingPass] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);
    const [current, setCurrent] = useState("");
    const [next1, setNext1] = useState("");
    const [next2, setNext2] = useState("");
    const [emailInput, setEmailInput] = useState("");
    const [strict, setStrict] = useState(() => {
        try { return localStorage.getItem(STRICT_KEY) === "1"; }
        catch { return false; }
    });

    const load = useCallback(async () => {
        try {
            const data = await api.getPlatformPasscodeConfig();
            setCfg(data);
            setEmailInput(data.recovery_email || "");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load passcode settings");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const changePass = async () => {
        if (next1.length < 8) {
            toast.error("New passcode must be at least 8 characters");
            return;
        }
        if (next1 !== next2) {
            toast.error("New passcodes don't match");
            return;
        }
        setSavingPass(true);
        try {
            await api.changePlatformPasscode(current, next1);
            toast.success("Passcode updated");
            setCurrent(""); setNext1(""); setNext2("");
            // Clear unlock so user re-enters with new passcode on next switch
            try { sessionStorage.removeItem("tcr_platform_unlock_v1"); } catch { /* ignore */ }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't change passcode");
        }
        setSavingPass(false);
    };

    const saveEmail = async () => {
        setSavingEmail(true);
        try {
            await api.setPlatformPasscodeRecoveryEmail(emailInput.trim());
            toast.success("Recovery email saved");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save email");
        }
        setSavingEmail(false);
    };

    const toggleStrict = (val) => {
        setStrict(val);
        try {
            if (val) localStorage.setItem(STRICT_KEY, "1");
            else localStorage.removeItem(STRICT_KEY);
        } catch { /* ignore */ }
        if (val) {
            // Force re-prompt next time
            try { sessionStorage.removeItem("tcr_platform_unlock_v1"); } catch { /* ignore */ }
        }
        toast.success(val
            ? "Strict mode on — passcode required on every Platform scope switch."
            : "Strict mode off — unlock persists for this browser session.");
    };

    return (
        <div className="space-y-6" data-testid="platform-passcode-manager">
            <div>
                <h3 className="font-display font-extrabold text-xl uppercase flex items-center gap-2">
                    <Lock className="w-5 h-5 text-volt" />
                    Platform admin passcode
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                    Gates entry into Commons Admin scope. Change anytime — only takes effect on next switch.
                </p>
            </div>

            {/* Change passcode */}
            <div className="brutal-card p-4 space-y-3">
                <h4 className="text-sm font-extrabold uppercase tracking-wider text-zinc-300 flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-cyber" /> Change passcode
                </h4>
                <Field label="Current passcode">
                    <input
                        type="password"
                        data-testid="platform-passcode-current-input"
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        className="brutal-input w-full text-sm font-mono"
                        autoComplete="current-password"
                    />
                </Field>
                <Field label="New passcode (min 8 chars)">
                    <input
                        type="password"
                        data-testid="platform-passcode-new-input"
                        value={next1}
                        onChange={(e) => setNext1(e.target.value)}
                        className="brutal-input w-full text-sm font-mono"
                        autoComplete="new-password"
                    />
                </Field>
                <Field label="Confirm new passcode">
                    <input
                        type="password"
                        data-testid="platform-passcode-confirm-input"
                        value={next2}
                        onChange={(e) => setNext2(e.target.value)}
                        className="brutal-input w-full text-sm font-mono"
                        autoComplete="new-password"
                    />
                </Field>
                <div className="flex justify-end">
                    <button
                        onClick={changePass}
                        disabled={savingPass || !current || !next1 || !next2}
                        data-testid="platform-passcode-save-btn"
                        className="brutal-btn bg-volt text-white text-xs py-2 px-3 disabled:opacity-40"
                    >
                        <Save className="w-3.5 h-3.5" /> {savingPass ? "Saving…" : "Change passcode"}
                    </button>
                </div>
            </div>

            {/* Recovery email */}
            <div className="brutal-card p-4 space-y-3">
                <h4 className="text-sm font-extrabold uppercase tracking-wider text-zinc-300 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-cyber" /> Recovery email
                </h4>
                <p className="text-xs text-zinc-500 leading-relaxed">
                    If you ever forget the passcode, the recovery email gets a 30-minute magic link to set a new one.
                    Use a personal/secondary email — not the admin login email — so a compromised primary account
                    can't self-reset.
                </p>
                <Field label="Recovery email">
                    <input
                        type="email"
                        data-testid="platform-passcode-recovery-input"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        placeholder="you@personal.com"
                        className="brutal-input w-full text-sm font-mono"
                    />
                </Field>
                <div className="flex items-center justify-between">
                    <p className="text-[10px] text-zinc-500">
                        {cfg?.recovery_email ? <>Current: <span className="font-mono text-cyber">{cfg.recovery_email}</span></> : <span className="text-heat">No recovery email set — set one before you forget the passcode.</span>}
                    </p>
                    <button
                        onClick={saveEmail}
                        disabled={savingEmail || !emailInput.trim() || emailInput.trim() === cfg?.recovery_email}
                        data-testid="platform-passcode-email-save-btn"
                        className="brutal-btn bg-cyber text-black text-xs py-2 px-3 disabled:opacity-40"
                    >
                        <Save className="w-3.5 h-3.5" /> {savingEmail ? "Saving…" : "Save email"}
                    </button>
                </div>
            </div>

            {/* Strict-mode toggle */}
            <div className="brutal-card p-4 space-y-3">
                <h4 className="text-sm font-extrabold uppercase tracking-wider text-zinc-300 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-cyber" /> Require passcode every switch
                </h4>
                <p className="text-xs text-zinc-500 leading-relaxed">
                    Off (default): one unlock per browser session — switch in and out freely until you close the tab.<br />
                    On: passcode required every time you switch <em>into</em> Platform scope. Use on shared computers.
                </p>
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        data-testid="platform-passcode-strict-toggle"
                        checked={strict}
                        onChange={(e) => toggleStrict(e.target.checked)}
                    />
                    <span className="text-sm text-zinc-300">
                        {strict
                            ? <strong className="text-volt">Strict mode ON</strong>
                            : <span>Strict mode off</span>}
                    </span>
                </label>
            </div>

            {!cfg?.is_set && (
                <div className="brutal-card p-4 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-heat shrink-0 mt-0.5" />
                    <p className="text-xs text-zinc-300">
                        No passcode is set in the database yet — the env-var fallback (PLATFORM_ADMIN_PASSCODE)
                        is in effect. Change the passcode above to lock it in.
                    </p>
                </div>
            )}
        </div>
    );
}


function Field({ label, children }) {
    return (
        <div>
            <p className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">{label}</p>
            {children}
        </div>
    );
}
